import { assert } from "@lib/utils/utils";
import Matrix from "./matrix";
import { singleLayerSProcess } from "./singleLayerSProcess";
import Vector from "./vector";

/**
 * Run the Single-layer S-Process for each layer of evaluators.
 * @param parentBudgets - The budgets of each funder.
 * @param preferenceListLayers - The preference lists of each layer of evaluators.
 *   - First dimension is layer (there are n_layers layers).
 *   - Second dimension is evaluator (there are n_evaluators evaluators, which
 *     varies depending on the layer).
 *   - Each evaluator has a preference list, which is an n_tranches x n_evaluables
 *     matrix.
 * @returns The final accounts and guarantees for each layer. For each layer,
 * the accounts and guarantees are n_evaluators x n_evaluables matrices, so
 * the size changes between layers, and there are n_layers matrices in total.
 */
export function multiLayerSProcess(
  parentBudgets: Vector<"funder">,
  preferenceListLayers: [
    Matrix<"tranche", "recommender+hold">[],
    Matrix<"tranche", "proposal+hold">[],
  ],
): {
  layerAccounts: [
    Matrix<"funder", "recommender+hold">,
    Matrix<"recommender+hold", "proposal+hold">,
  ];
  layerGuarantees: [
    Matrix<"funder", "recommender+hold">,
    Matrix<"recommender+hold", "proposal+hold">,
  ];
  totalEpochs: number;
  layerBudgets: [Vector<"funder">, Vector<"recommender">];
} {
  let totalEpochs = 0;

  // start layer 0
  const layer0PreferenceLists = preferenceListLayers[0];

  const {
    accounts: layer0Accounts,
    guarantees: layer0Guarantees,
    epochs: layer0Epochs,
  } = singleLayerSProcess(
    parentBudgets.rename("funder -> evaluator"),
    layer0PreferenceLists.map((p) =>
      p.rename("tranche -> tranche", "recommender+hold -> evaluable+hold"),
    ),
  );

  const layer0NumEvaluators = layer0PreferenceLists.length;
  const layer0NumEvaluables = layer0PreferenceLists[0].n_cols;
  assert(
    layer0Accounts.n_rows === layer0NumEvaluators,
    "accounts.n_rows should be equal to numEvaluators",
  );
  assert(
    layer0Guarantees.n_rows === layer0NumEvaluators,
    "guarantees.n_rows should be equal to numEvaluators",
  );
  assert(
    layer0Accounts.n_cols === layer0NumEvaluables,
    "accounts.n_cols should be equal to numEvaluables",
  );
  assert(
    layer0Guarantees.n_cols === layer0NumEvaluables,
    "guarantees.n_cols should be equal to numEvaluables",
  );

  const layerAccounts0: Matrix<"funder", "recommender+hold"> =
    layer0Accounts.rename(
      "evaluator -> funder",
      "evaluable+hold -> recommender+hold",
    );
  const layerGuarantees0: Matrix<"funder", "recommender+hold"> =
    layer0Guarantees.rename(
      "evaluator -> funder",
      "evaluable+hold -> recommender+hold",
    );
  totalEpochs += layer0Epochs;

  // Collapse rows, preserve columns = Amount flowing into each
  // evaluable (which becomes the new budget for the next layer),
  // shift to remove amount allocated to the hold proposal
  const recommenderBudgets: Vector<"recommender"> = layer0Accounts
    .sum(0)
    .shift()[1]
    .rename("evaluable -> recommender");
  // end layer 0

  // start layer 1
  const layer1PreferenceLists = preferenceListLayers[1];

  const {
    accounts: layer1Accounts,
    guarantees: layer1Guarantees,
    epochs: layer1Epochs,
  } = singleLayerSProcess(
    recommenderBudgets.rename("recommender -> evaluator"),
    layer1PreferenceLists.map((p) =>
      p.rename("tranche -> tranche", "proposal+hold -> evaluable+hold"),
    ),
  );

  const layer1NumEvaluators = layer1PreferenceLists.length;
  const layer1NumEvaluables = layer1PreferenceLists[0].n_cols;
  assert(
    layer1Accounts.n_rows === layer1NumEvaluators,
    "accounts.n_rows should be equal to numEvaluators",
  );
  assert(
    layer1Guarantees.n_rows === layer1NumEvaluators,
    "guarantees.n_rows should be equal to numEvaluators",
  );
  assert(
    layer1Accounts.n_cols === layer1NumEvaluables,
    "accounts.n_cols should be equal to numEvaluables",
  );
  assert(
    layer1Guarantees.n_cols === layer1NumEvaluables,
    "guarantees.n_cols should be equal to numEvaluables",
  );

  const layerAccounts1: Matrix<"recommender+hold", "proposal+hold"> =
    layer1Accounts.rename(
      "evaluator -> recommender+hold",
      "evaluable+hold -> proposal+hold",
    );
  const layerGuarantees1: Matrix<"recommender+hold", "proposal+hold"> =
    layer1Guarantees.rename(
      "evaluator -> recommender+hold",
      "evaluable+hold -> proposal+hold",
    );
  totalEpochs += layer1Epochs;
  // end layer 1

  return {
    layerAccounts: [layerAccounts0, layerAccounts1],
    layerGuarantees: [layerGuarantees0, layerGuarantees1],
    totalEpochs,
    layerBudgets: [parentBudgets, recommenderBudgets],
  };
}
